import React, { useState, useEffect, useCallback, useMemo } from 'react';
import redditService from '../services/redditService';
import aiDebugService from '../services/aiDebugService';

const CompanyTag = ({ company, stockData, item, customPrompts, analyzeWithAI }) => {
  const [showMenu, setShowMenu] = useState(false);

  const handlePromptSelect = (prompt) => {
    analyzeWithAI(item, prompt, company);
    setShowMenu(false);
  };

  const formatMarketCap = (marketCap) => {
    if (!marketCap) return 'N/A';
    if (marketCap >= 1e12) return `$${(marketCap / 1e12).toFixed(2)}T`;
    if (marketCap >= 1e9) return `$${(marketCap / 1e9).toFixed(2)}B`;
    if (marketCap >= 1e6) return `$${(marketCap / 1e6).toFixed(2)}M`;
    return `$${marketCap.toFixed(0)}`;
  };

  const formatChange = (change) => {
    if (!change) return '';
    const isPositive = change > 0;
    return (
      <span className={`daily-change ${isPositive ? 'positive' : 'negative'}`}>
        {isPositive ? '+' : ''}{change.toFixed(2)}%
      </span>
    );
  };

  return (
    <div className="company-stock-card">
      <div className="stock-card-header" onClick={() => setShowMenu(!showMenu)}>
        <div className="company-main-info">
          {stockData?.logo && (
            <img 
              src={stockData.logo} 
              alt={`${company} logo`}
              className="company-logo"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <div className="company-text">
            <span className="company-symbol">{company}</span>
            {stockData?.name && (
              <span className="company-name">{stockData.name}</span>
            )}
          </div>
        </div>
        {stockData?.price && (
          <div className="stock-price-main">
            <span className="stock-price">${stockData.price.toFixed(2)}</span>
            {formatChange(stockData.changePercent)}
          </div>
        )}
      </div>

      {stockData && (
        <div className="stock-details">
          <div className="stock-row">
            <span className="stock-label">Market Cap:</span>
            <span className="stock-value">
              {formatMarketCap(stockData.marketCap)}
            </span>
          </div>
          <div className="stock-row">
            <span className="stock-label">Day Range:</span>
            <span className="stock-value">
              {stockData.low && stockData.high ? 
                `${stockData.low.toFixed(2)} - ${stockData.high.toFixed(2)}` : 
                'N/A'
              }
            </span>
          </div>
          <div className="stock-row">
            <span className="stock-label">Sector:</span>
            <span className="stock-value">
              {stockData.sector || 'N/A'}
            </span>
          </div>
          <div className="stock-row">
            <span className="stock-label">Volume:</span>
            <span className="stock-value">
              {stockData.volume ? stockData.volume.toLocaleString() : 'N/A'}
            </span>
          </div>
          
          <div className="external-links-section">
            <div className="external-links-label">VIEW ON:</div>
            <div className="external-links">
              <a 
                href={`https://finance.yahoo.com/quote/${company}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="external-link yahoo"
                title="View on Yahoo Finance"
              >
                <svg className="external-icon" viewBox="0 0 24 24" width="16" height="16">
                  <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <span>Yahoo</span>
              </a>
              
              <a 
                href={`https://www.nasdaq.com/market-activity/stocks/${company.toLowerCase()}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="external-link nasdaq"
                title="View on Nasdaq"
              >
                <svg className="external-icon" viewBox="0 0 24 24" width="16" height="16">
                  <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <span>Nasdaq</span>
              </a>
              
              <a 
                href={`https://www.tradingview.com/symbols/${company}`}
                target="_blank"
                rel="noopener noreferrer"
                className="external-link tradingview"
                title="View on TradingView"
              >
                <svg className="external-icon" viewBox="0 0 24 24" width="16" height="16">
                  <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <span>TradingView</span>
              </a>
            </div>
          </div>
        </div>
      )}
      
      {showMenu && (
        <div className="company-prompt-menu">
          <div className="prompt-menu-header">
            <span>Select AI Analysis for {company}</span>
            <button 
              className="close-menu-button"
              onClick={() => setShowMenu(false)}
            >
              ✕
            </button>
          </div>
          <div className="prompt-options">
            {customPrompts.map((prompt, idx) => (
              <button
                key={idx}
                className="prompt-option"
                onClick={() => handlePromptSelect(prompt.prompt)}
              >
                {prompt.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SocialFeeds = ({ userApiKeys, userSettings, openaiApiKey, customPrompts }) => {
  const [socialPosts, setSocialPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Extract finnhubApiKey from userApiKeys
  const finnhubApiKey = userApiKeys?.finnhubApiKey || process.env.REACT_APP_FINNHUB_API_KEY || 'd3aijrhr01qlsbrrjh00d3aijrhr01qlsbrrjh0g';
  const [selectedSource, setSelectedSource] = useState('all');
  const [sortBy, setSortBy] = useState('score');
  const [postCount, setPostCount] = useState(25);
  const [detectedStocks, setDetectedStocks] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [analyzingItems, setAnalyzingItems] = useState(new Set());
  const [aiResponses, setAiResponses] = useState({});

  // Configure Reddit service
  useEffect(() => {
    redditService.setConfig({
      enabled: userApiKeys?.redditEnabled === true,
      apiKey: userApiKeys?.redditApiKey || null
    });
  }, [userApiKeys]);

  // Company patterns for stock detection (similar to NewsFeeds)
  const companyPatterns = useMemo(() => ({
    'AAPL': ['Apple', 'iPhone', 'iPad', 'Mac', 'iOS', 'App Store', 'Tim Cook'],
    'MSFT': ['Microsoft', 'Windows', 'Office', 'Azure', 'Teams', 'Xbox'],
    'GOOGL': ['Google', 'Alphabet', 'YouTube', 'Android', 'Chrome', 'Search'],
    'AMZN': ['Amazon', 'AWS', 'Prime', 'Alexa', 'Bezos'],
    'TSLA': ['Tesla', 'Elon Musk', 'Model S', 'Model 3', 'Model Y', 'Cybertruck'],
    'META': ['Meta', 'Facebook', 'Instagram', 'WhatsApp', 'Metaverse'],
    'NVDA': ['Nvidia', 'GeForce', 'RTX', 'AI chips', 'graphics card'],
    'NFLX': ['Netflix', 'streaming service', 'Reed Hastings'],
    'JPM': ['JPMorgan', 'JP Morgan', 'Chase', 'Jamie Dimon'],
    'BAC': ['Bank of America', 'BofA'],
    'WMT': ['Walmart', 'Sam\'s Club'],
    'JNJ': ['Johnson & Johnson', 'J&J'],
    'PG': ['Procter & Gamble', 'P&G'],
    'KO': ['Coca-Cola', 'Coke'],
    'PEP': ['PepsiCo', 'Pepsi'],
    'DIS': ['Disney', 'Disney+', 'ESPN', 'Marvel', 'Star Wars'],
    'V': ['Visa'],
    'MA': ['Mastercard'],
    'HD': ['Home Depot'],
    'UNH': ['UnitedHealth', 'United Health'],
    'PFE': ['Pfizer'],
    'ABBV': ['AbbVie'],
    'TMO': ['Thermo Fisher'],
    'COST': ['Costco'],
    'ABT': ['Abbott'],
    'NKE': ['Nike'],
    'CRM': ['Salesforce'],
    'ADBE': ['Adobe'],
    'ORCL': ['Oracle'],
  }), []);

  // Detect stock symbols in text
  const detectStockSymbols = useCallback((title, content) => {
    const text = `${title} ${content}`.toLowerCase();
    const detected = [];
    
    for (const [symbol, patterns] of Object.entries(companyPatterns)) {
      for (const pattern of patterns) {
        if (text.includes(pattern.toLowerCase())) {
          detected.push(symbol);
          break;
        }
      }
    }
    
    // Also look for $SYMBOL format
    const symbolMatches = text.match(/\$([A-Z]{1,5})\b/g);
    if (symbolMatches) {
      symbolMatches.forEach(match => {
        const symbol = match.substring(1);
        if (!detected.includes(symbol)) {
          detected.push(symbol);
        }
      });
    }
    
    return [...new Set(detected)];
  }, [companyPatterns]);

  // Fetch stock data for detected symbols
  const fetchStockData = useCallback(async (symbols) => {
    const stockData = {};
    
    // Determine which service to use based on user preferences
    const useYahooFinance = userApiKeys?.yahooFinanceEnabled === true;
    const useFinHub = userApiKeys?.finnhubEnabled === true && finnhubApiKey && !useYahooFinance;
    
    for (const symbol of symbols) {
      try {
        let data = null;
        
        if (useYahooFinance) {
          // Use Yahoo Finance 2 via backend API
          const response = await fetch(`http://localhost:3001/api/yahoo-quote/${symbol}`);
          if (response.ok) {
            data = await response.json();
            if (data && data.price) {
              stockData[symbol] = {
                symbol,
                price: data.price,
                change: data.change,
                changePercent: data.changePercent,
                high: data.high,
                low: data.low,
                open: data.open,
                previousClose: data.previousClose,
                volume: data.volume,
                marketCap: data.marketCap,
                pe: data.pe,
                dividendYield: data.dividendYield,
                timestamp: Date.now()
              };
            }
          }
        } else if (useFinHub) {
          // Use FinHub API
          const response = await fetch(`https://finnhub.io/api/v1/quote?symbol=${symbol}&token=${finnhubApiKey}`);
          if (response.ok) {
            data = await response.json();
            if (data && data.c) {
              stockData[symbol] = {
                symbol,
                price: data.c,
                change: data.d,
                changePercent: data.dp,
                high: data.h,
                low: data.l,
                open: data.o,
                previousClose: data.pc,
                timestamp: Date.now()
              };
            }
          }
        }
      } catch (error) {
        console.error(`Error fetching stock data for ${symbol}:`, error);
      }
    }
    
    return stockData;
  }, [finnhubApiKey, userApiKeys]);

  // Fetch Reddit posts
  const fetchSocialPosts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!userApiKeys?.redditEnabled) {
        setSocialPosts([]);
        return;
      }

      // Default subreddits for finance
      const defaultSubreddits = ['stocks', 'investing', 'SecurityAnalysis', 'StockMarket'];
      const searchTerms = ['AAPL', 'Tesla', 'dividend', 'earnings'];
      
      // Calculate items per source to get approximately the desired total
      const itemsPerSource = Math.ceil(postCount / 2); // Split between subreddits and search
      
      // Fetch from subreddits
      const subredditPosts = await redditService.getMultipleSubredditPosts(defaultSubreddits, {
        limit: itemsPerSource,
        sort: 'hot'
      });
      
      // Fetch search results
      const searchPosts = await redditService.searchMultipleQueries(searchTerms, {
        limit: itemsPerSource,
        sort: 'relevance'
      });
      
      // Combine and deduplicate
      const allPosts = [...subredditPosts, ...searchPosts];
      const uniquePosts = allPosts.filter((post, index, self) => 
        index === self.findIndex(p => p.id === post.id)
      );
      
      // Sort by publication date (newest first)
      uniquePosts.sort((a, b) => new Date(b.created) - new Date(a.created));
      
      // Limit to the exact post count requested
      const limitedPosts = uniquePosts.slice(0, postCount);
      
      // Detect stocks in posts
      const postsWithStocks = limitedPosts.map(post => ({
        ...post,
        detectedStocks: detectStockSymbols(post.title, post.content)
      }));
      
      // Fetch stock data for all detected symbols
      const allDetectedStocks = [...new Set(postsWithStocks.flatMap(post => post.detectedStocks))];
      const stockData = await fetchStockData(allDetectedStocks);
      
      setDetectedStocks(stockData);
      setSocialPosts(postsWithStocks);
      setLastUpdated(new Date());
      
    } catch (error) {
      console.error('Error fetching social posts:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [userApiKeys, detectStockSymbols, fetchStockData, postCount]);

  // AI Analysis function
  const analyzeWithAI = async (item, prompt, selectedSymbol = null) => {
    if (!openaiApiKey) {
      setError('Please configure your OpenAI API key in Settings first.');
      return;
    }

    const itemId = item.id;
    setAnalyzingItems(prev => new Set([...prev, itemId]));

    // Use the selected symbol or the first detected company
    const symbol = selectedSymbol || (item.detectedStocks && item.detectedStocks[0]) || 'UNKNOWN';
    
    // Replace placeholders in the prompt
    const processedPrompt = prompt
      .replace('{SYMBOL}', symbol)
      .replace('{COMPANY}', symbol);

    // Determine which model to use based on prompt type
    const isMasterPrompt = prompt.includes('{COMPANY}') && prompt.includes('financial analyst');
    const selectedModel = isMasterPrompt ? 
      aiDebugService.getMasterPromptModel() : 
      aiDebugService.getCustomPromptModel();

    try {
      const requestBody = {
        model: selectedModel,
        messages: [
          {
            role: 'system',
            content: 'You are a financial analyst AI assistant. Provide detailed, accurate, and professional analysis. Always include disclaimers that this is not financial advice.'
          },
          {
            role: 'user',
            content: `${processedPrompt}\n\nSocial Post: ${item.title}\nContent: ${item.content || 'No additional content'}`
          }
        ],
        max_tokens: 1000,
        temperature: 0.7
      };

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${openaiApiKey}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('OpenAI API Error Response (Analysis):', errorText);
        throw new Error(`OpenAI API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      const aiResponse = data.choices[0].message.content;
      
      // Log debug information
      const debugType = isMasterPrompt ? 'master-prompt' : 'custom-prompt';
      aiDebugService.logAiRequest(
        debugType,
        selectedModel,
        `${processedPrompt}\n\nSocial Post: ${item.title}\nContent: ${item.content || 'No additional content'}`,
        aiResponse
      );
      
      setAiResponses(prev => ({
        ...prev,
        [itemId]: aiResponse
      }));
    } catch (err) {
      console.error('Error calling OpenAI API:', err);
      
      // Log error for debugging
      const debugType = isMasterPrompt ? 'master-prompt' : 'custom-prompt';
      aiDebugService.logAiRequest(
        debugType,
        selectedModel,
        `${processedPrompt}\n\nSocial Post: ${item.title}\nContent: ${item.content || 'No additional content'}`,
        null,
        err.message
      );
      
      setError(`Failed to get AI analysis: ${err.message}`);
    } finally {
      setAnalyzingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchSocialPosts();
  }, [fetchSocialPosts]);

  // Filter and sort posts
  const filteredPosts = useMemo(() => {
    let filtered = socialPosts;
    
    if (selectedSource !== 'all') {
      filtered = filtered.filter(post => post.sourceName === selectedSource);
    }
    
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'score':
          return b.score - a.score;
        case 'date':
          return new Date(b.created) - new Date(a.created);
        case 'comments':
          return b.numComments - a.numComments;
        default:
          return 0;
      }
    });
  }, [socialPosts, selectedSource, sortBy]);

  // Get unique sources
  const sources = useMemo(() => {
    const uniqueSources = [...new Set(socialPosts.map(post => post.sourceName))];
    return uniqueSources.sort();
  }, [socialPosts]);

  const formatDate = (date) => {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openInNewTab = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const getFilteredPosts = () => {
    if (selectedSource === 'all') {
      return filteredPosts;
    }
    return filteredPosts.filter(post => post.sourceName === selectedSource);
  };

  if (!userApiKeys?.redditEnabled) {
    return (
      <div className="page-content">
        <div className="finance-source">
          <div className="finance-header">
            <h2>📱 Social Feeds</h2>
            <p>Financial discussions and sentiment from Reddit communities</p>
          </div>
          <div className="finance-content">
            <div className="error-container">
              <h3>🔒 Social Feeds Disabled</h3>
              <p>Reddit integration is not enabled. Go to Settings → Integrations & Modules → Reddit to enable it.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="finance-source">
        <div className="finance-header">
          <h2>📱 Social Feeds</h2>
          <p>Financial discussions and sentiment from Reddit communities</p>
          {lastUpdated && (
            <div className="last-updated">
              Last updated: {formatDate(lastUpdated)}
            </div>
          )}
        </div>
        
        <div className="finance-content">
          {loading && socialPosts.length === 0 ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading social feeds...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <h3>⚠️ Error Loading Social Feeds</h3>
              <p>{error}</p>
              <button 
                className="retry-button"
                onClick={fetchSocialPosts}
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="news-container">
              <div className="news-header">
                <h3>Latest Social Posts</h3>
                <div className="news-controls">
                  <div className="feed-filter">
                    <label htmlFor="source-select">Filter by source:</label>
                    <select 
                      id="source-select"
                      value={selectedSource} 
                      onChange={(e) => setSelectedSource(e.target.value)}
                      className="feed-select"
                    >
                      <option value="all">All Sources</option>
                      {sources.map(source => (
                        <option key={source} value={source}>{source}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="article-count-filter">
                    <label htmlFor="post-count-select">Posts to load:</label>
                    <select 
                      id="post-count-select"
                      value={postCount}
                      onChange={(e) => setPostCount(parseInt(e.target.value))}
                      className="feed-select"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                  
                  <div className="article-count-filter">
                    <label htmlFor="sort-select">Sort by:</label>
                    <select 
                      id="sort-select"
                      value={sortBy} 
                      onChange={(e) => setSortBy(e.target.value)}
                      className="feed-select"
                    >
                      <option value="score">Score</option>
                      <option value="date">Date</option>
                      <option value="comments">Comments</option>
                    </select>
                  </div>
                  
                  <div className="refresh-controls">
                    <label className="auto-refresh-toggle">
                      <input 
                        type="checkbox" 
                        checked={true} 
                        readOnly
                      />
                      Auto-refresh
                    </label>
                    <button 
                      className="refresh-button"
                      onClick={fetchSocialPosts}
                      disabled={loading}
                    >
                      {loading ? 'Refreshing...' : 'Refresh Now'}
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="news-list">
                {getFilteredPosts().map((post, index) => (
                  <div key={post.id || index} className="news-item">
                    <div className="news-item-header">
                      <h4 
                        className="news-title"
                        onClick={() => openInNewTab(post.url)}
                      >
                        {post.title}
                      </h4>
                      <div className="news-item-meta">
                        <span className="news-date">{formatDate(new Date(post.created))}</span>
                        <button 
                          className="read-more-button"
                          onClick={() => openInNewTab(post.url)}
                        >
                          READ MORE →
                        </button>
                      </div>
                    </div>
                    
                    <div className="news-source-section">
                      <span className="news-source-tag" style={{ backgroundColor: '#8B5CF6' }}>
                        {post.sourceName}
                      </span>
                      <span className="post-author">by u/{post.author}</span>
                      <span className="post-stats">⬆️ {post.score} | 💬 {post.numComments}</span>
                    </div>
                    
                    {post.content && (
                      <div className="news-content-text">
                        <p>
                          {post.content.length > 300 
                            ? post.content.substring(0, 300) + '...' 
                            : post.content}
                        </p>
                      </div>
                    )}
                    
                    {post.flair && (
                      <div className="news-flair">
                        <span className="flair-tag">{post.flair}</span>
                      </div>
                    )}
                    
                    {post.detectedStocks && post.detectedStocks.length > 0 && (
                      <div className="company-tags-section">
                        <div className="company-tags">
                          {post.detectedStocks.map(symbol => (
                            <CompanyTag 
                              key={symbol} 
                              company={symbol} 
                              stockData={detectedStocks[symbol]} 
                              item={post}
                              customPrompts={customPrompts || []}
                              analyzeWithAI={analyzeWithAI}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {analyzingItems.has(post.id) && (
                      <div className="ai-loading">
                        <div className="ai-spinner"></div>
                        <span>Analyzing with AI...</span>
                      </div>
                    )}
                    
                    {aiResponses[post.id] && (
                      <div className="ai-response-content">
                        <div className="ai-response-header">
                          <span className="ai-response-title">AI Analysis Result</span>
                          <button 
                            className="close-ai-response"
                            onClick={() => {
                              setAiResponses(prev => {
                                const newResponses = { ...prev };
                                delete newResponses[post.id];
                                return newResponses;
                              });
                            }}
                          >
                            ✕
                          </button>
                        </div>
                        <div className="ai-response-text">
                          {aiResponses[post.id].split('\n').map((paragraph, idx) => (
                            <p key={idx}>{paragraph}</p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SocialFeeds;